import { BaseCrudStore } from '@monorepo/controlled/src/stores/base-crud.store';
import { FormError } from '@monorepo/tools/src/lib/models/form-error.model';
import { DestinationsApi } from '../../apis/destinations.api';
import { HttpError } from '../../models/http-error.model';
import { DestinationModel, IDestinationCreateForm, IDestinationEditForm } from '../../models/destination.model';
import { validateDestUrlForAccount } from '../../utils/destination-validation';

export class DestinationCrudStore extends BaseCrudStore<DestinationModel, IDestinationCreateForm, IDestinationEditForm, HttpError> {
	constructor() {
		super({
			apiLayer: DestinationsApi,
			model: DestinationModel,
			errorModel: HttpError,
		});
	}

	createInstance() {
		return new DestinationModel({
			dest_url: '',
			yid: '',
			destination_prices: [],
		});
	}

	public getCreateFormData(): IDestinationCreateForm {
		const advertiserId = this.getData().getAdvertiser()?.getId();
		const account = this.getData().getAccount();
		const body: IDestinationCreateForm = {
			name: this.getData().getName() || '',
			advertiser: { id: advertiserId! },
			account: {
				id: account!.getId()!,
				name: account!.getName(),
				use_internal_pricing: account!.getUseInternalPricing(),
			},
			dest_url: this.getData().getDestUrl(),
			yid: this.getData().getYid() || undefined,
		};

		if (this.getData().usesExternalPricing()) {
			body.destination_prices = this.getData()
				.getDestinationPricing()
				.map(row => row.toObject());
		}

		return body;
	}

	public getEditFormData(): IDestinationEditForm {
		const body: IDestinationEditForm = {
			name: this.getData().getName() || '',
			dest_url: this.getData().getDestUrl(),
			yid: this.getData().getYid() || undefined,
		};

		if (this.getData().usesExternalPricing()) {
			body.destination_prices = this.getData()
				.getDestinationPricing()
				.map(row => row.toObject());
		}

		body.creatives = this.getData()
			.getCreatives()
			.filter(link => link.getCreative()?.getId() != null)
			.map(link => ({
				id: link.getId(),
				status: link.getStatus(),
				weight: link.weight,
				creative: { id: link.getCreative()!.getId()! },
			}));

		return body;
	}

	public isValid(): boolean {
		this.formStore.reset();
		const destination = this.getData();
		const isEdit = Boolean(destination.getId());
		const account = destination.getAccount();
		const useInternalPricing = account?.getUseInternalPricing();

		if (!destination.getName()?.trim()) {
			this.formStore.addError(new FormError('name', 'Please provide a name'));
		}

		if (!isEdit) {
			if (!destination.getAdvertiser()?.getId()) {
				this.formStore.addError(new FormError('advertiser', 'Please select an advertiser'));
			}
			if (!account?.getId()) {
				this.formStore.addError(new FormError('account', 'Please select an account'));
			}
		}

		const destUrlError = validateDestUrlForAccount(destination.getDestUrl(), useInternalPricing);
		if (destUrlError) {
			this.formStore.addError(new FormError('dest_url', destUrlError));
		}

		if (useInternalPricing === false) {
			const pricingRows = destination.getDestinationPricing();
			if (pricingRows.length === 0) {
				this.formStore.addError(new FormError('destination_prices', 'Please add at least one payout row'));
			}

			const geos = new Set<string>();
			pricingRows.forEach((row, index) => {
				const geo = row.getGeo()?.trim();
				if (!geo) {
					this.formStore.addError(new FormError(`destination_prices_geo_${index}`, 'Please provide a geo'));
				} else if (geos.has(geo.toUpperCase())) {
					this.formStore.addError(new FormError(`destination_prices_geo_${index}`, 'Geo must be unique'));
				} else {
					geos.add(geo.toUpperCase());
				}

				if (!row.getDefaultPayout() || row.getDefaultPayout() <= 0) {
					this.formStore.addError(
						new FormError(`destination_prices_payout_${index}`, 'Please provide a default payout greater than 0')
					);
				}
			});
		}

		return this.formStore.getIsValid();
	}
}
